import { Combobox as BaseUiCombobox } from "@base-ui/react/combobox"
import React, { useCallback, useContext, useMemo } from "react"
import { get, useController, useFormState } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useBaseUiClassName } from "shared/lib/useBaseUiClassName"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { IconSize } from "shared/types/Icon"
import { SelectOption } from "shared/types/Select"
import { Icon as UiKitIcon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { Input as DefaultInput, InputContext, InputRootProps } from "../Input"
import { useInputDescriptionIDs } from "../accessibility/useInputAriaDescription"
import { ComboboxContext, ComboboxContextType } from "./ComboboxContext"
import { ReadonlyCombobox } from "./ReadonlyCombobox"
import "./styles/_combobox.scss"

// Base UI control types
type SelectValue = string | number | boolean | null | undefined

export interface ComboboxRootProps<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends SelectValue = any,
  Multiple extends boolean | undefined = false
> extends InputRootProps,
    ComboboxContextType {
  value?: SelectOption<T> | SelectOption<T>[] | null
  onValueChange?: (value: SelectOption<T> | SelectOption<T>[] | null) => void
  items?: SelectOption<T>[]
  multiple?: Multiple
}

const ControlBase = BaseUiCombobox.Root
const { Portal } = BaseUiCombobox
const { useFilter } = BaseUiCombobox

function RootComponent<
  T extends SelectValue,
  Multiple extends boolean | undefined = false
>(
  {
    className,
    elevated,
    creatable,
    value,
    onValueChange,
    items,
    multiple,
    disabled,
    ...props
  }: ComboboxRootProps<T, Multiple>,
  ref?: React.ForwardedRef<HTMLDivElement>
) {
  const context = useMemo(
    () => ({
      elevated,
      creatable,
      value,
      onValueChange,
      items,
      multiple,
      disabled
    }),
    [elevated, creatable, value, onValueChange, items, multiple, disabled]
  )

  return (
    <ComboboxContext.Provider value={context}>
      <DefaultInput.Root
        {...props}
        disabled={disabled}
        className={classNames(
          "combobox-input",
          { "input--elevated": elevated },
          className
        )}
        ref={ref}
      />
    </ComboboxContext.Provider>
  )
}

const Root = React.memo(React.forwardRef(RootComponent)) as typeof RootComponent

interface ComboboxControlBaseProps<
  T extends SelectValue,
  Multiple extends boolean | undefined = false
> extends Omit<
    BaseUiCombobox.Root.Props<SelectOption<T>, Multiple>,
    "value" | "items" | "multiple" | "onValueChange" | "disabled"
  > {}

function ComboboxControlBase<
  T extends SelectValue = SelectValue,
  Multiple extends boolean | undefined = false
>(rest: ComboboxControlBaseProps<T, Multiple>) {
  const { disabled: inputContextDisabled, readonly } = useContext(InputContext)
  const {
    value,
    onValueChange,
    items,
    multiple,
    disabled: contextDisabled
  } = useContext(ComboboxContext)

  const derivedValue = useMemo(() => {
    if (value === null) {
      return multiple ? [] : null
    }
    return value
  }, [value, multiple])

  if (readonly) {
    return null
  }

  return (
    <BaseUiCombobox.Root
      {...rest}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value={derivedValue as any}
      onValueChange={onValueChange}
      items={items}
      multiple={multiple as Multiple}
      disabled={contextDisabled || inputContextDisabled}
    />
  )
}

interface HookFormComboboxControlProps<
  T extends SelectValue,
  Multiple extends boolean | undefined = false
> extends Omit<
    ComboboxControlBaseProps<T, Multiple>,
    "value" | "onValueChange" | "name"
  > {}

function HookFormComboboxControlComponent<T extends SelectValue = SelectValue>({
  ...rest
}: HookFormComboboxControlProps<T>) {
  const { name, required } = useContext(InputContext)
  const existingComboboxContext = useContext(ComboboxContext)
  const { items, multiple, disabled } = existingComboboxContext
  const { errors } = useFormState({ name })
  const error = get(errors, name as string)

  const {
    field: { ref: fieldRef, value: fieldValue = null, onChange, ...field }
  } = useController({ name: name as string, disabled, rules: { required } })

  const value = useMemo(() => {
    if (multiple) {
      if (!Array.isArray(fieldValue) || fieldValue.length === 0) {
        return []
      }
      return fieldValue
        .map((val: T) => items?.find(opt => opt.value === val))
        .filter(
          (opt: SelectOption<T> | undefined): opt is SelectOption<T> =>
            opt !== undefined
        )
    }

    return items?.find(opt => opt.value === fieldValue) ?? null
  }, [fieldValue, items, multiple])

  // Convert Combobox onChange to form onChange
  const handleChange = useCallback(
    (selectedValue: SelectOption<T>[] | SelectOption<T> | null) => {
      if (multiple) {
        // Multi-select: SelectOption<T>[] → T[] | null
        const arrayValue = selectedValue as SelectOption<T>[] | null
        if (!arrayValue || arrayValue.length === 0) {
          onChange([])
          return
        }
        const values = arrayValue.map(opt => opt.value)
        onChange(values)
        return
      }

      // Single-select: SelectOption<T> | null → T | null
      const singleValue = selectedValue as SelectOption<T> | null
      if (!singleValue) {
        onChange(null)
        return
      }
      onChange(singleValue.value)
    },
    [onChange, multiple]
  )

  // Merge existing context with form-specific values (including computed value/onValueChange)
  const comboboxContextValue = useMemo(
    () => ({
      ...existingComboboxContext,
      fieldRef,
      hasError: !!error,
      value,
      onValueChange: handleChange as ComboboxContextType["onValueChange"]
    }),
    [existingComboboxContext, fieldRef, error, value, handleChange]
  )

  return (
    <ComboboxContext.Provider value={comboboxContextValue}>
      <ComboboxControlBase {...rest} {...field} />
    </ComboboxContext.Provider>
  )
}

const HookFormComboboxControl = HookFormComboboxControlComponent as <
  T extends SelectValue = SelectValue,
  Multiple extends boolean | undefined = false
>(
  props: HookFormComboboxControlProps<T, Multiple>
) => React.ReactElement

// Smart Control component that auto-detects form mode
function ComboboxControlComponent<
  T extends SelectValue,
  Multiple extends boolean | undefined = false
>(props: ComboboxControlBaseProps<T>) {
  const { name } = useContext(InputContext)

  if (name) {
    return (
      <HookFormComboboxControl
        {...(props as HookFormComboboxControlProps<T, Multiple>)}
      />
    )
  }

  return <ComboboxControlBase {...props} />
}

const ComboboxControl = React.memo(ComboboxControlComponent) as <
  T extends SelectValue = SelectValue,
  Multiple extends boolean | undefined = false
>(
  props: ComboboxControlBaseProps<T, Multiple>
) => React.ReactElement

const Input = React.forwardRef<HTMLInputElement, BaseUiCombobox.Input.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__input", className)
    return (
      <BaseUiCombobox.Input {...restProps} ref={ref} className={getClassName} />
    )
  }
)
Input.displayName = "ComboboxInput"

const InputContainer = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const { className, children, ...restProps } = props
  return (
    <div
      {...restProps}
      ref={ref}
      className={classNames("input", "combobox__input-container")}
    >
      <div className="input__focus">{children}</div>
    </div>
  )
})
InputContainer.displayName = "ComboboxInputContainer"

function Value(props: BaseUiCombobox.Value.Props): React.ReactElement {
  const { children, placeholder, ...restProps } = props
  const { t } = useTranslation()

  const defaultRender = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (currentValue: any) => {
      // Empty or null value
      if (!currentValue) {
        return placeholder ? (
          <span className="combobox__placeholder">{placeholder}</span>
        ) : (
          ""
        )
      }

      // Multi-select: array of SelectOption objects
      if (Array.isArray(currentValue)) {
        if (currentValue.length === 0) {
          return ""
        }

        const firstLabel = currentValue[0]?.label ?? ""
        const additionalCount = currentValue.length - 1

        if (additionalCount > 0) {
          return `${firstLabel} (+${additionalCount} ${t("Select.More")})`
        }

        return firstLabel
      }

      // Single-select: SelectOption object
      return currentValue?.label ?? ""
    },
    [t, placeholder]
  )

  return (
    <BaseUiCombobox.Value {...restProps}>
      {children ?? defaultRender}
    </BaseUiCombobox.Value>
  )
}
Value.displayName = "ComboboxValue"

const Trigger = React.forwardRef<
  HTMLButtonElement,
  BaseUiCombobox.Trigger.Props
>((props, ref) => {
  const { className, ...restProps } = props
  const { id, label, disabled } = useContext(InputContext)
  const { fieldRef, hasError } = useContext(ComboboxContext)
  const { ariaDescribedBy } = useInputDescriptionIDs({
    additionalDescriptions: restProps["aria-describedby"]
  })

  // Merge external ref with field ref from react-hook-form
  const mergedRef = useMergedRefs(ref, fieldRef)

  const getClassName = useBaseUiClassName(
    classNames(
      "combobox__trigger",
      "combobox",
      "input__focus",
      "input__control",
      { error: hasError }
    ),
    className
  )

  return (
    <BaseUiCombobox.Trigger
      {...restProps}
      ref={mergedRef}
      id={id}
      aria-label={label}
      aria-describedby={ariaDescribedBy}
      disabled={disabled}
      className={getClassName}
    />
  )
})
Trigger.displayName = "ComboboxTrigger"

const Icon = React.forwardRef<HTMLDivElement, BaseUiCombobox.Icon.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__icon", className)
    return (
      <BaseUiCombobox.Icon
        {...restProps}
        ref={ref}
        className={getClassName}
        render={
          restProps.render || (
            <span className="select__icon" ref={ref}>
              <UiKitIcon name={IconName.SMALL_CARET_DOWN} size={IconSize.XXS} />
            </span>
          )
        }
      />
    )
  }
)
Icon.displayName = "ComboboxIcon"

const Positioner = React.forwardRef<
  HTMLDivElement,
  BaseUiCombobox.Positioner.Props
>((props, ref) => {
  const {
    className,
    positionMethod = "fixed",
    collisionPadding = 24,
    ...restProps
  } = props
  const getClassName = useBaseUiClassName("combobox__positioner", className)
  return (
    <BaseUiCombobox.Positioner
      {...restProps}
      ref={ref}
      className={getClassName}
      positionMethod={positionMethod}
      collisionPadding={collisionPadding}
      align="start"
      data-input=""
    />
  )
})
Positioner.displayName = "ComboboxPositioner"

const Popup = React.forwardRef<HTMLDivElement, BaseUiCombobox.Popup.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const { elevated } = useContext(ComboboxContext)
    const getClassName = useBaseUiClassName(
      classNames("combobox__popup", {
        "combobox__popup--elevated": elevated
      }),
      className
    )
    return (
      <BaseUiCombobox.Popup {...restProps} ref={ref} className={getClassName} />
    )
  }
)
Popup.displayName = "ComboboxPopup"

const Backdrop = React.forwardRef<
  HTMLDivElement,
  BaseUiCombobox.Backdrop.Props
>((props, ref) => {
  const { className, ...restProps } = props
  const getClassName = useBaseUiClassName("combobox__backdrop", className)
  return (
    <BaseUiCombobox.Backdrop
      {...restProps}
      ref={ref}
      className={getClassName}
    />
  )
})
Backdrop.displayName = "ComboboxBackdrop"

const Arrow = React.forwardRef<HTMLDivElement, BaseUiCombobox.Arrow.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__arrow", className)
    return (
      <BaseUiCombobox.Arrow {...restProps} ref={ref} className={getClassName} />
    )
  }
)
Arrow.displayName = "ComboboxArrow"

const List = React.forwardRef<HTMLDivElement, BaseUiCombobox.List.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__list", className)
    return (
      <BaseUiCombobox.List {...restProps} ref={ref} className={getClassName} />
    )
  }
)
List.displayName = "ComboboxList"

const Item = React.forwardRef<HTMLDivElement, BaseUiCombobox.Item.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__item", className)
    return (
      <BaseUiCombobox.Item {...restProps} ref={ref} className={getClassName} />
    )
  }
)
Item.displayName = "ComboboxItem"

const Create = React.forwardRef<HTMLDivElement, BaseUiCombobox.Item.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName(
      "combobox__item combobox__create",
      className
    )
    return (
      <BaseUiCombobox.Item
        {...restProps}
        ref={ref}
        className={getClassName}
        render={(p, state) => {
          // eslint-disable-next-line no-console
          console.log(p, state)
          return <div />
        }}
      />
    )
  }
)
Create.displayName = "ComboboxCreate"

const ItemIndicator = React.forwardRef<
  HTMLSpanElement,
  BaseUiCombobox.ItemIndicator.Props
>((props, ref) => {
  const { className, ...restProps } = props
  const getClassName = useBaseUiClassName("combobox__item-indicator", className)
  return (
    <BaseUiCombobox.ItemIndicator
      {...restProps}
      ref={ref}
      className={getClassName}
      render={
        restProps.render || (
          <span className="select__item-indicator">
            <UiKitIcon name={IconName.CHECK_SMALL} size={IconSize.SMALL} />
          </span>
        )
      }
    />
  )
})
ItemIndicator.displayName = "ComboboxItemIndicator"

const ItemContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const { className, ...restProps } = props
  return (
    <div
      {...restProps}
      ref={ref}
      className={classNames("combobox__item-content", className)}
    />
  )
})
ItemContent.displayName = "ComboboxItemContent"

const Group = React.forwardRef<HTMLDivElement, BaseUiCombobox.Group.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__group", className)
    return (
      <BaseUiCombobox.Group {...restProps} ref={ref} className={getClassName} />
    )
  }
)
Group.displayName = "ComboboxGroup"

const GroupLabel = React.forwardRef<
  HTMLDivElement,
  BaseUiCombobox.GroupLabel.Props
>((props, ref) => {
  const { className, ...restProps } = props
  const getClassName = useBaseUiClassName("combobox__group-label", className)
  return (
    <BaseUiCombobox.GroupLabel
      {...restProps}
      ref={ref}
      className={getClassName}
    />
  )
})
GroupLabel.displayName = "ComboboxGroupLabel"

const Empty = React.forwardRef<HTMLDivElement, BaseUiCombobox.Empty.Props>(
  (props, ref) => {
    const { t } = useTranslation()
    const { className, children, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__empty", className)
    return (
      <BaseUiCombobox.Empty {...restProps} ref={ref} className={getClassName}>
        {children ?? t("Search.NoResults")}
      </BaseUiCombobox.Empty>
    )
  }
)
Empty.displayName = "ComboboxEmpty"

const Status = React.forwardRef<HTMLDivElement, BaseUiCombobox.Status.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__status", className)
    return (
      <BaseUiCombobox.Status
        {...restProps}
        ref={ref}
        className={getClassName}
      />
    )
  }
)
Status.displayName = "ComboboxStatus"

const Chips = React.forwardRef<HTMLDivElement, BaseUiCombobox.Chips.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__chips", className)
    return (
      <BaseUiCombobox.Chips {...restProps} ref={ref} className={getClassName} />
    )
  }
)
Chips.displayName = "ComboboxChips"

const Chip = React.forwardRef<HTMLDivElement, BaseUiCombobox.Chip.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__chip", className)
    return (
      <BaseUiCombobox.Chip {...restProps} ref={ref} className={getClassName} />
    )
  }
)
Chip.displayName = "ComboboxChip"

const ChipRemove = React.forwardRef<
  HTMLButtonElement,
  BaseUiCombobox.ChipRemove.Props
>((props, ref) => {
  const { className, ...restProps } = props
  const getClassName = useBaseUiClassName("combobox__chip-remove", className)
  return (
    <BaseUiCombobox.ChipRemove
      {...restProps}
      ref={ref}
      className={getClassName}
    />
  )
})
ChipRemove.displayName = "ComboboxChipRemove"

const Clear = React.forwardRef<HTMLButtonElement, BaseUiCombobox.Clear.Props>(
  (props, ref) => {
    const { className, ...restProps } = props
    const getClassName = useBaseUiClassName("combobox__clear", className)
    return (
      <BaseUiCombobox.Clear {...restProps} ref={ref} className={getClassName} />
    )
  }
)
Clear.displayName = "ComboboxClear"

export const Combobox = {
  ...DefaultInput,
  Root,
  Control: ComboboxControl,
  ControlBase,
  Input,
  InputContainer,
  Trigger,
  Value,
  Icon,
  Portal,
  Positioner,
  Popup,
  Backdrop,
  Arrow,
  List,
  Item,
  ItemIndicator,
  ItemContent,
  Create,
  Group,
  GroupLabel,
  Empty,
  Status,
  Chips,
  Chip,
  ChipRemove,
  Clear,
  Readonly: ReadonlyCombobox,
  useFilter
}
